//* Libraries imports
import { useNavigate, useOutletContext } from "react-router-dom";
import { useState, useEffect, useMemo } from "react";
import { AlertTriangle } from "lucide-react";

//* Components Imports
import {
  PaymentHeader,
  PaymentOrderDetails,
  PaymentSupport,
  PaymentMethods,
  PaymentFooter,
  CpfModal,
  PaymentDuplicateModal,
} from "./components/home";

//* Helpers
import { getStatusBgColor, getStatusLabel } from "@/helpers/paymentHelper";
import { getSaleFinancialConfig } from "@helpers/saleHelper";
import { PaymentStatus } from "@/schemas/payment";

//* Types
import {
  MethodPaymentEnum,
  type MethodPaymentDTO,
} from "@/schemas/methodpayment";
import type { ISaleOutputDTO } from "@/schemas/sales";
import type { Client } from "@/schemas";

//* Hooks
import { useCreateCustomer } from "@/hooks/asaas/clients/mutations/useCreateCustomer";

const WHATSAPP_NUMBER = "+5517992460986";

export function PaymentHome() {
  const navigate = useNavigate();
  const { sale, refetchSale } = useOutletContext<{
    sale: ISaleOutputDTO | null;
    refetchSale: (id: string) => Promise<ISaleOutputDTO | null>;
  }>();

  const availableMethods = useMemo(() => {
    const config = getSaleFinancialConfig(sale);
    if (config?.paymentMethods && config.paymentMethods.length > 0) {
      return config.paymentMethods;
    }
    return [
      MethodPaymentEnum.PIX,
      MethodPaymentEnum.CREDIT_CARD,
      MethodPaymentEnum.BOLETO,
      MethodPaymentEnum.TED,
    ];
  }, [sale]);

  const [selectedPaymentMethod, setSelectedPaymentMethod] =
    useState<MethodPaymentDTO>(MethodPaymentEnum.PIX);

  useEffect(() => {
    if (
      availableMethods.length > 0 &&
      !availableMethods.includes(selectedPaymentMethod)
    ) {
      setSelectedPaymentMethod(availableMethods[0]);
    }
  }, [availableMethods, selectedPaymentMethod]);

  const {
    create: createCustomer,
    loading: creatingCustomer,
    error: createCustomerError,
  } = useCreateCustomer();
  const [isCpfModalOpen, setIsCpfModalOpen] = useState<boolean>(false);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState<boolean>(false);

  const handleWhatsAppPress = (phone?: string) => {
    const cleanPhone = (phone || WHATSAPP_NUMBER).replace(/\D/g, "");
    const message = encodeURIComponent("Olá!");
    const url = `https://wa.me/${cleanPhone}?text=${message}`;
    window.open(url, "_blank");
  };

  const proceedToPayment = (customerId: string) => {
    switch (selectedPaymentMethod) {
      case MethodPaymentEnum.PIX:
        navigate("pix", { state: { customerId } });
        break;
      case MethodPaymentEnum.BOLETO:
        navigate("boleto", { state: { customerId } });
        break;
      case MethodPaymentEnum.CREDIT_CARD:
        navigate("credit", { state: { customerId } });
        break;
      case MethodPaymentEnum.TED:
        navigate("ted", { state: { customerId } });
        break;
      default:
        console.error("Método de pagamento não suportado");
    }
  };

  const getClient = () => {
    if (!sale?.clientId || typeof sale.clientId !== "object") return null;
    return sale.clientId as Client;
  };

  const createCustomerFromClient = async (
    client: Client,
    cpf: string,
    wasCpfNull?: boolean,
  ) => {
    if (!client._id || !client.name || !cpf.replace(/\D/g, "")) return null;

    const newCustomer = await createCustomer({
      name: client.name,
      email: client.email || "",
      cpfCnpj: cpf.replace(/\D/g, ""),
      mobilePhone: (client.whatsapp || "").replace(/\D/g, ""),
      wasCpfNull,
      clientId: client._id,
    });

    return newCustomer?.id;
  };

  const paymentStatus = sale?.paymentStatus || "CREATED";
  const isInteractiveStatus = [
    "CREATED",
    "PENDING",
    "TED",
    "CHECKOUT_VIEWED",
  ].includes(paymentStatus);

  const hasPendingPayment =
    (paymentStatus === "PENDING" ||
      paymentStatus === "OVERDUE" ||
      paymentStatus === "TED" ||
      paymentStatus === "AWAITING_RISK_ANALYSIS") &&
    sale?.paymentDetails &&
    (sale.paymentDetails.method === MethodPaymentEnum.PIX ||
      sale.paymentDetails.method === MethodPaymentEnum.BOLETO ||
      sale.paymentDetails.method === MethodPaymentEnum.TED);

  const executePayment = async () => {
    const client = getClient();
    if (!client) return console.error("Client data not fully loaded");

    if (client.customerId) return proceedToPayment(client.customerId);
    if (client.cpf) {
      try {
        const id = await createCustomerFromClient(client, client.cpf);
        if (id) {
          if (sale?._id) await refetchSale(sale._id);
          proceedToPayment(id);
        }
      } catch (e) {
        console.error("Erro ao criar cliente:", e);
      }
      return;
    }
    setIsCpfModalOpen(true);
  };

  const handleCpfConfirm = async (cpf: string) => {
    const client = getClient();
    if (!client) return;

    try {
      const id = await createCustomerFromClient(client, cpf, true);
      if (id) {
        if (sale?._id) await refetchSale(sale._id);
        setIsCpfModalOpen(false);
        proceedToPayment(id);
      }
    } catch (e) {
      console.error("Erro ao criar cliente via modal:", e);
    }
  };

  const handleStartPayment = async () => {
    if (hasPendingPayment) {
      setIsConfirmModalOpen(true);
      return;
    }
    await executePayment();
  };

  return (
    <div className="flex-1 flex flex-col bg-claroborg-202 min-h-screen items-center py-8 px-4 gap-6">
      <PaymentHeader />

      <main className="w-full max-w-3xl bg-white rounded-lg border border-clarobordas-01 shadow-sm p-5 md:p-8 flex flex-col gap-6">
        {hasPendingPayment && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex gap-3 animate-in fade-in slide-in-from-top-2">
            <AlertTriangle className="size-5 text-amber-600 shrink-0" />
            <div className="flex-1">
              <h4 className="text-sm font-semibold text-amber-800 mb-1">
                Pagamento Pendente
              </h4>
              <p className="text-sm text-amber-700 leading-relaxed">
                Já existe uma cobrança gerada para esta venda. Caso já tenha
                efetuado o pagamento, aguarde a compensação bancária.
              </p>
            </div>
          </div>
        )}
        <PaymentOrderDetails sale={sale} />
        <div className="h-px w-full bg-clarobordas-01" />
        <PaymentSupport
          onWhatsAppClick={handleWhatsAppPress}
          responsible={sale?.responsibleId}
        />
        <div className="h-px w-full bg-clarobordas-01" />
        {isInteractiveStatus ? (
          <PaymentMethods
            selectedMethod={selectedPaymentMethod}
            onSelectMethod={setSelectedPaymentMethod}
            allowedMethods={availableMethods}
          />
        ) : (
          <div className="flex w-full justify-center">
            <div
              className={`flex items-center justify-center rounded-full border px-4 py-1.5 text-sm font-medium ${getStatusBgColor(
                paymentStatus as PaymentStatus,
              )}`}
            >
              {getStatusLabel(paymentStatus as PaymentStatus)}
            </div>
          </div>
        )}
      </main>

      {isInteractiveStatus && (
        <PaymentFooter
          onStartPayment={handleStartPayment}
          isLoading={creatingCustomer}
        />
      )}

      <CpfModal
        open={isCpfModalOpen}
        onClose={() => setIsCpfModalOpen(false)}
        onConfirm={handleCpfConfirm}
        loading={creatingCustomer}
        error={createCustomerError}
      />

      <PaymentDuplicateModal
        open={isConfirmModalOpen}
        onOpenChange={setIsConfirmModalOpen}
        onConfirm={executePayment}
      />
    </div>
  );
}
